import { Box, ButtonBase } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { JSX } from "react";

import type { SalesOrgLeadDetail } from "@xop/dashboard-shared";

import { salesApi } from "../../client.js";
import { buildAddToCrmPayload } from "../crmPayload.js";
import { lifecycleActions, statusLabel } from "../leadStatus.js";

/* ── Lifecycle action bar (operator pipeline moves) ─────────────── */

export function LifecycleBar({ lead }: { lead: SalesOrgLeadDetail }): JSX.Element {
  const leadId = lead.id;
  const status = lead.status;
  const qc = useQueryClient();
  const [pending, setPending] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: (next: string) => salesApi.setLeadStatus(leadId, next),
    onMutate: (next: string) => setPending(next),
    onSettled: () => setPending(null),
    onSuccess: () => {
      // Refresh both this lead and the (possibly now-filtered) list.
      void qc.invalidateQueries({ queryKey: ["sales", "leads"] });
    },
  });

  // "Qualify for CRM" — push this lead into the CRM as a real prospect (a CRM
  // Lead tagged "AI created prospect"), which the CRM shows as a "qualified"
  // AI prospect. Builds the payload from the lead + its primary contact via the
  // shared `buildAddToCrmPayload` (also used by the leads-list row action, so
  // the two can't drift); on success the lead flips to a done-state "Qualified"
  // pill (crm_pushed_at set). A salesperson personally qualifying a lead is the
  // strongest "good lead" signal the agent learns from.
  const crmMut = useMutation({
    // Builds the all-contacts payload (every named buyer + generic inbox) via
    // the shared `buildAddToCrmPayload` — the SAME helper the leads-list row
    // action uses, so the two can't drift.
    mutationFn: () => salesApi.addLeadToCrm(leadId, buildAddToCrmPayload(lead)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["sales", "leads"] });
    },
  });
  const inCrm = Boolean(lead.crm_pushed_at);
  // Don't offer "Qualify for CRM" once it's pushed, or once it's already a
  // customer (converted) — the header badge covers the converted case.
  const showConvert = !lead.crm_pushed_at && !lead.converted_at;

  // The sensible moves for the current stage.
  const actions = lifecycleActions(status);

  return (
    <Box
      component="section"
      className="no-print"
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        boxShadow: (t: Theme) => t.shadows[1],
        p: 2,
      }}
    >
      <Box sx={{ fontSize: 12, color: "text.disabled" }}>
        Pipeline status:{" "}
        <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
          {statusLabel(status)}
        </Box>
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
        {inCrm ? (
          <Box
            component="span"
            title="This lead has been qualified into the CRM as a prospect (tagged 'AI created prospect')."
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, borderRadius: 1, border: 1, borderColor: "divider", px: 1.5, py: 0.75, fontSize: 12, fontWeight: 600, color: "text.secondary" }}
          >
            ✓ Qualified
          </Box>
        ) : showConvert ? (
          <ButtonBase
            disabled={crmMut.isPending}
            onClick={() => crmMut.mutate()}
            title="Qualify this lead into the CRM as a prospect — the strongest 'good lead' signal the agent learns from."
            sx={{ borderRadius: 1, px: 1.5, py: 0.75, fontSize: 12, fontWeight: 600, bgcolor: "primary.main", color: "primary.contrastText", transition: "filter 150ms", "&:hover": { filter: "brightness(0.95)" }, "&.Mui-disabled": { opacity: 0.5 } }}
          >
            {crmMut.isPending ? "Qualifying…" : "★ Qualify for CRM"}
          </ButtonBase>
        ) : null}
        {crmMut.isError ? (
          <Box component="span" sx={{ fontSize: 11, color: "error.main" }}>Couldn't qualify for CRM — try again</Box>
        ) : null}
        {actions.map((a) => (
          <ButtonBase
            key={a.status}
            disabled={mut.isPending}
            onClick={() => mut.mutate(a.status)}
            sx={[
              { borderRadius: 1, border: 1, px: 1.5, py: 0.75, fontSize: 12, fontWeight: 500, transition: "background-color 150ms", "&.Mui-disabled": { opacity: 0.5 } },
              a.danger
                ? {
                    borderColor: (t: Theme) => alpha(t.palette.error.main, 0.5),
                    color: "error.main",
                    "&:hover": { bgcolor: (t: Theme) => alpha(t.palette.error.main, 0.1) },
                  }
                : { borderColor: "divider", color: "text.primary", "&:hover": { bgcolor: "surfaceSoft" } },
            ]}
          >
            {pending === a.status ? "…" : a.label}
          </ButtonBase>
        ))}
      </Box>
    </Box>
  );
}
